import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  DollarSign, TrendingUp, TrendingDown, Users, Briefcase, Rocket, Eye, Send,
  RefreshCw, Repeat, Wallet, Percent, Clock,
} from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import { KpiCard, GlassCard, PageHeader, PrimaryButton } from "@/components/Primitives";
import ActivityFeed from "@/components/ActivityFeed";
import RangeSelect, { DEFAULT_RANGE } from "@/components/RangeSelect";
import ExportButton from "@/components/ExportButton";

const tooltipStyle = {
  contentStyle: {
    background: "rgba(15,15,18,0.92)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 14,
    backdropFilter: "blur(20px)",
    color: "#fff",
    fontSize: 12,
  },
  labelStyle: { color: "rgba(255,255,255,0.5)" },
};

const COLORS = ["#007AFF", "#34C759", "#FF9F0A", "#BF5AF2", "#5AC8FA", "#FF6482", "#FFD60A", "#30D158", "#64D2FF", "#FF453A"];

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [range, setRange] = useState(() => localStorage.getItem("equinox_dashboard_range") || DEFAULT_RANGE);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    localStorage.setItem("equinox_dashboard_range", range);
    let cancelled = false;
    (async () => {
      const [k, c] = await Promise.all([
        api.get("/dashboard/kpis", { params: { range } }),
        api.get("/dashboard/charts", { params: { range } }),
      ]);
      if (cancelled) return;
      setKpis(k.data);
      setCharts(c.data);
    })();
    return () => { cancelled = true; };
  }, [range, tick]);

  const refresh = () => setTick((t) => t + 1);

  if (!kpis || !charts) {
    return <div className="text-white/40">Loading…</div>;
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle={`Your business at a glance — ${kpis.range_label?.toLowerCase() || "all time"}.`}
        testId="dashboard-header"
        action={
          <div className="flex items-center gap-2.5">
            <RangeSelect value={range} onChange={setRange} testIdPrefix="dashboard-range" />
            <ExportButton endpoint="/exports/revenue" range={range} testId="dashboard-export-revenue" label="Export" />
            <PrimaryButton onClick={refresh} data-testid="dashboard-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </PrimaryButton>
          </div>
        }
      />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard testId="kpi-revenue" label="Revenue" value={fmtMoney(kpis.total_revenue)} icon={DollarSign} accent="from-emerald-400 to-emerald-600" hint={kpis.range_label} />
        <KpiCard testId="kpi-expenses" label="Expenses" value={fmtMoney(kpis.total_expenses)} icon={TrendingDown} accent="from-rose-400 to-rose-600" hint={kpis.range_label} />
        <KpiCard testId="kpi-net-profit" label="Net Profit" value={fmtMoney(kpis.net_profit)} icon={TrendingUp} accent="from-[#007AFF] to-[#0050C8]" hint={`${kpis.gross_margin}% margin`} />
        <KpiCard testId="kpi-mrr" label="Total MRR" value={fmtMoney(kpis.total_mrr)} icon={Repeat} accent="from-violet-400 to-violet-600" hint="Point-in-time" />

        <KpiCard testId="kpi-active-clients" label="Active Clients" value={kpis.active_clients} icon={Users} accent="from-cyan-400 to-cyan-600" />
        <KpiCard testId="kpi-active-projects" label="Active Projects" value={kpis.active_projects} icon={Briefcase} accent="from-amber-400 to-amber-600" hint={`${kpis.completed_projects} completed`} />
        <KpiCard testId="kpi-pipeline-value" label="Pipeline Value" value={fmtMoney(kpis.pipeline_value)} icon={Wallet} accent="from-indigo-400 to-indigo-600" />
        <KpiCard testId="kpi-close-rate" label="Close Rate" value={`${kpis.close_rate}%`} icon={Percent} accent="from-teal-400 to-teal-600" hint={`${kpis.won_deals || 0} won · ${kpis.lost_deals || 0} lost`} />
      </div>

      {/* Secondary KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        {[
          ["In Development", kpis.projects_in_development, Briefcase],
          ["In Review", kpis.projects_in_review, Eye],
          ["Deployed", kpis.projects_deployed, Rocket],
          ["Hosting MRR", fmtMoney(kpis.hosting_mrr), Repeat],
          ["Maintenance MRR", fmtMoney(kpis.maintenance_mrr), Clock],
          ["Avg. Project", fmtMoney(kpis.average_project_value), Send],
        ].map(([label, value, Icon], i) => (
          <GlassCard key={i} className="p-5" testId={`mini-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</div>
              <Icon className="w-3.5 h-3.5 text-white/30" />
            </div>
            <div className="font-display text-2xl font-light">{value}</div>
          </GlassCard>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <GlassCard className="lg:col-span-2" testId="chart-revenue">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Revenue · Last 12 months</div>
              <div className="font-display text-2xl font-light mt-1">Revenue vs. Profit</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={charts.revenue_chart}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#007AFF" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34C759" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#34C759" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}
                     tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} cursor={{ stroke: "rgba(0,122,255,0.4)", strokeWidth: 1, fill: "transparent" }} />
              <Area type="monotone" dataKey="revenue" stroke="#007AFF" strokeWidth={2} fill="url(#rev)" />
              <Area type="monotone" dataKey="profit" stroke="#34C759" strokeWidth={2} fill="url(#prof)" />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard testId="chart-project-status">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Projects</div>
            <div className="font-display text-2xl font-light mt-1">Status mix</div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={charts.project_chart} dataKey="count" nameKey="status" innerRadius={55} outerRadius={88} stroke="none">
                {charts.project_chart.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip {...tooltipStyle} cursor={{ fill: "transparent" }} />
              <Legend wrapperStyle={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>

      <GlassCard testId="chart-pipeline">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Pipeline</div>
          <div className="font-display text-2xl font-light mt-1">Value by stage</div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={charts.pipeline_chart}>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}
                   tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
            <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} cursor={{ fill: "rgba(0,122,255,0.08)" }} />
            <Bar dataKey="value" fill="#007AFF" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </GlassCard>

      <GlassCard className="mt-6" testId="dashboard-activity">
        <ActivityFeed limit={12} />
      </GlassCard>
    </div>
  );
}
